import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getWebSocketUrl } from '../lib/config';

// ==========================================
// Types
// ==========================================

interface WebSocketStore {
  socket: WebSocket | null;
  isConnected: boolean;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  shopId: string | null;

  // Actions
  connect: (shopId: string, onMessage: (event: MessageEvent) => void) => void;
  disconnect: () => void;
  setConnected: (connected: boolean) => void;
}

// ==========================================
// Store
// ==========================================

export const useWebSocketStore = create<WebSocketStore>()(
  devtools(
    (set, get) => ({
      socket: null,
      isConnected: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 10,
      shopId: null,

      connect: (shopId: string, onMessage: (event: MessageEvent) => void) => {
        const state = get();

        // Don't reconnect if already connected to same shop
        if (state.socket?.readyState === WebSocket.OPEN && state.shopId === shopId) {
          return;
        }

        // Close existing connection
        if (state.socket) {
          state.socket.close();
        }

        try {
          // Use centralized WebSocket URL configuration
          const wsUrl = getWebSocketUrl(shopId, 'cashier');
          const ws = new WebSocket(wsUrl);

          ws.onopen = () => {
            console.log('[Cashier WS] Connected');
            set({ isConnected: true, reconnectAttempts: 0 });
          };

          ws.onmessage = onMessage;

          ws.onclose = () => {
            console.log('[Cashier WS] Disconnected');
            set({ isConnected: false });

            // Attempt reconnection with exponential backoff
            const { reconnectAttempts, maxReconnectAttempts } = get();
            if (reconnectAttempts < maxReconnectAttempts) {
              const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
              console.log(`[Cashier WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1})`);

              setTimeout(() => {
                set({ reconnectAttempts: reconnectAttempts + 1 });
                get().connect(shopId, onMessage);
              }, delay);
            }
          };

          ws.onerror = (event) => {
            console.error('[Cashier WS] Error:', event);
          };

          set({ socket: ws, shopId });
        } catch (err) {
          console.error('[Cashier WS] Failed to connect:', err);
        }
      },

      disconnect: () => {
        const { socket } = get();
        if (socket) {
          socket.close();
        }
        set({
          socket: null,
          isConnected: false,
          reconnectAttempts: 0,
          shopId: null,
        });
      },

      setConnected: (connected: boolean) => {
        set({ isConnected: connected });
      },
    }),
    { name: 'CashierWebSocket' }
  )
);
